import { reducerWithInitialState } from "typescript-fsa-reducers";

import withImmer from "../../lib/withImmer";

import {
  bootstrapCompanyShow,
  bootstrapCompanyAbout,
  bootstrapCompanyMembers,
  bootstrapCompanyPosts,
  bootstrapCompanyProjects,
  loadCompanyShow,
  loadCompanyAbout,
  loadCompanyMembers,
  loadCompanyPosts,
  loadCompanyProjects,
  loadSimilarCompanies,
  draftCompanyUpdated,
  draftCompanyAvatarUpdated,
  draftCompanyBackgroundUpdated,
  saveDraftAll,
  saveDraftCompany,
  editModeStarted,
  editModeFinished,
  draftReset,
} from "../actions";
import { Company, SimilarCompany } from "../api";

export interface CommonState {
  company?: Company | null; // Stored for reset. Do not update except on page loaded or draft saved.
  editMode: boolean;
  isDraftCompanyDirty: boolean;
  draftCompany?: Company;
  draftCompanyAvatar?: Company["avatar"];
  draftCompanyBackground?: Company["background"];
  isSavingDraft: boolean;
  similarCompanies: SimilarCompany[];
}

const initialState: CommonState = {
  company: null,
  editMode: false,
  isDraftCompanyDirty: false,
  isSavingDraft: false,
  similarCompanies: [],
};

function buildDraftCompany(company: Company | undefined) {
  const draftCompany = company;
  const draftCompanyAvatar = draftCompany ? draftCompany.avatar : undefined;
  const draftCompanyBackground = draftCompany ? draftCompany.background : undefined;
  return {
    draftCompany,
    draftCompanyAvatar,
    draftCompanyBackground,
  };
}

function initState(state: CommonState, company: Company): CommonState {
  state.company = company;
  state.isDraftCompanyDirty = false;
  Object.assign(state, buildDraftCompany(company));
  return state;
}

const commonReducer = withImmer(reducerWithInitialState<CommonState>(initialState))
  .cases(
    [
      bootstrapCompanyShow,
      bootstrapCompanyAbout,
      bootstrapCompanyMembers,
      bootstrapCompanyPosts,
      bootstrapCompanyProjects,
    ],
    (state, result) => initState(state, result.company)
  )
  .cases(
    [
      loadCompanyShow.done,
      loadCompanyAbout.done,
      loadCompanyMembers.done,
      loadCompanyPosts.done,
      loadCompanyProjects.done,
    ],
    (state, { result }) => initState(state, result.company)
  )
  .case(saveDraftCompany.done, (state, { result }) => initState(state, result))
  .case(draftReset, state => {
    state.isDraftCompanyDirty = false;
    Object.assign(state, buildDraftCompany(state.company!));
    return state;
  })
  .case(draftCompanyUpdated, (state, company) => {
    state.draftCompany = company;
    state.isDraftCompanyDirty = true;
    return state;
  })
  .case(draftCompanyAvatarUpdated, (state, image) => {
    state.draftCompanyAvatar = image;
    state.isDraftCompanyDirty = true;
    return state;
  })
  .case(draftCompanyBackgroundUpdated, (state, image) => {
    state.draftCompanyBackground = image;
    state.isDraftCompanyDirty = true;
    return state;
  })
  .case(saveDraftAll.started, state => {
    state.isSavingDraft = true;
    return state;
  })
  .case(saveDraftAll.done, state => {
    state.isSavingDraft = false;
    return state;
  })
  .case(saveDraftAll.failed, state => {
    state.isSavingDraft = false;
    return state;
  })
  // TODO(KentoMoriwaki): Handle saveDraftCompany.failed
  .case(editModeStarted, state => {
    state.editMode = true;
    return state;
  })
  .case(editModeFinished, state => {
    state.editMode = false;
    return state;
  })
  .case(loadSimilarCompanies.done, (state, { result }) => {
    state.similarCompanies = result;
    return state;
  });

export default commonReducer;



// WEBPACK FOOTER //
// ./frontend/src/companies/reducers/common.ts